/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Children, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { LAYOUT2 } from '../Styles';

const MASONRY_GRID_STYLE = css`
  label: MASONRY_GRID_STYLE;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const MASONRY_COLUMN_STYLE = css`
  label: MASONRY_COLUMN_STYLE;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const MASONRY_COLUMN_STRETCH_STYLE = css`
  label: MASONRY_COLUMN_STRETCH_STYLE;
  flex: 1;
`;

/**
 * A responsive masonry grid that distributes children across columns in round-robin fashion.
 *
 * @param minColumnWidth - Minimum width for each column in pixels (default: 300). Determines the number of columns.
 * @param maxColumnWidth - Maximum width for each column in pixels. If the stretched width would exceed this, columns are fixed at maxColumnWidth instead of stretching. If undefined, columns stretch freely.
 * @param maxColumns - Maximum number of columns (default: 4)
 * @param gap - Gap between columns and items in pixels (default: LAYOUT2.spacing2)
 */
export const MasonryGrid = ({
  children,
  minColumnWidth = 450,
  maxColumnWidth,
  maxColumns,
  gap = LAYOUT2.spacing2,
  backgroundColor,
  padding,
}: {
  children: ReactNode;
  minColumnWidth?: number;
  maxColumnWidth?: number;
  maxColumns?: number;
  gap?: number;
  backgroundColor?: string;
  padding?: string;
}) => {
  const childArray = Children.toArray(children);
  if (!maxColumns || childArray.length > maxColumns) maxColumns = childArray.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(1);
  const [shouldStretch, setShouldStretch] = useState(true);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateLayout = () => {
      const containerWidth = container.offsetWidth;

      // Column count is determined purely by minColumnWidth
      const columnsThatFit = Math.floor((containerWidth + gap) / (minColumnWidth + gap));
      const count = Math.max(1, Math.min(columnsThatFit, maxColumns));
      setColumnCount(count);

      // Determine if we should stretch or use fixed width
      if (maxColumnWidth) {
        // Calculate what the column width would be if we stretched
        const stretchedColumnWidth = (containerWidth - (count - 1) * gap) / count;
        setShouldStretch(stretchedColumnWidth <= maxColumnWidth);
      } else {
        setShouldStretch(true);
      }
    };

    updateLayout();

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [minColumnWidth, maxColumnWidth, maxColumns, gap]);

  // Distribute children across columns in round-robin fashion
  const columns: ReactNode[][] = Array.from({ length: columnCount }, () => []);

  childArray.forEach((child, index) => {
    columns[index % columnCount].push(child);
  });

  const dynamicGridStyle = css`
    gap: ${gap}px;
  `;

  const dynamicColumnStyle = css`
    gap: ${gap}px;
    ${!shouldStretch && maxColumnWidth ? `width: ${maxColumnWidth}px;` : ''}
  `;

  return (
    <div ref={containerRef} css={[MASONRY_GRID_STYLE, dynamicGridStyle]} style={{ backgroundColor, padding }}>
      {columns.map((columnChildren, columnIndex) => (
        <div key={columnIndex} css={[MASONRY_COLUMN_STYLE, shouldStretch && MASONRY_COLUMN_STRETCH_STYLE, dynamicColumnStyle]}>
          {columnChildren}
        </div>
      ))}
    </div>
  );
};
